// Focused error boundary for the top menu bar (AppMenuBar).
//
// Why this exists: the menu bar owns tab clicks and the Documents toggle. If
// a child throws during render — most often during dev/HMR when a stale
// import like `Folder is not defined` slips through — React unmounts the
// menu bar entirely and the app's tab system becomes unreachable. The user
// sees this as "I clicked a tab and nothing happened" with no obvious
// recovery path.
//
// With this boundary, the menu bar is replaced by a minimal black bar with
// a Reload button. One click brings the app back without the user having
// to know they need to hit Ctrl+Shift+R.
//
// Uses a class component because React error boundaries require it —
// `componentDidCatch` has no hook equivalent.
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface State {
  error: Error | null;
}

export class MenuBarErrorBoundary extends Component<
  { children: ReactNode },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log so the dev console still shows the failure — Vite HMR will print
    // the actual stack, and this confirms which surface caught it.
    console.error('[MenuBarErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="fixed left-0 right-0 top-0 z-50 hidden h-10 items-center justify-between border-b border-telekinesis-neutral-800 bg-black px-3 lg:flex">
        <span className="font-mono text-[11px] text-red-400">
          Menu bar crashed: {this.state.error.message}
        </span>
        <button
          onClick={() => window.location.reload()}
          className="rounded border border-white/20 px-2 py-0.5 text-[11px] text-telekinesis-text-primary transition-colors hover:border-white/40 hover:bg-white/5"
        >
          Reload
        </button>
      </div>
    );
  }
}
